import React from "react";
import type { StylesConfig } from "react-select";

export {
  PropertyConsultantContainer as PropertyConsultantWrapper,
  PropertyOwnerContainer as PropertyOwnerWrapper,
  RentIptuCondominiumValueAreaContainer as PropertyValuesWrapper,
  RoomsSuitesBathroomsGarageContainer as PropertyAccommodationsWrapper,
} from "../../../EditProperty/twStyles";

function cn(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(" ");
}

type DivProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
};

/** Rótulos de campo na descrição — mesmo peso/cor do label "Ranking". */
export const DESCRIPTION_FIELD_LABEL_CLASS =
  "mb-2 block text-sm font-semibold text-slate-700";

/** Cadastro: layout igual ao Edit, mas sem `border-0` + só `border-b` nos Selects (conflitava com borda única do formSelectStyles). */
export const PropertyOwnerConsultantWrapper: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-12 flex w-full items-start justify-start max-md:flex-col",
      "[&_.css-2b097c-container]:mt-3 [&_.css-2b097c-container]:w-full",
      "[&_[class*='-control']]:rounded-md [&_[class*='-control']]:shadow-none",
      className
    )}
    {...props}
  />
);

/** Área Sim ou Não dentro de `PermissionRadioWrapper` (segmented). */
export const PermissionRadioChoice: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { active?: boolean }
> = ({ className, active, ...props }) => (
  <div
    role="presentation"
    className={cn(
      "flex min-h-[40px] flex-1 cursor-pointer items-center justify-center gap-0.5 rounded-md px-1 transition",
      active ? "bg-slate-200/95" : "hover:bg-slate-50",
      className
    )}
    {...props}
  />
);

/** Linha Sim/Não: use dois filhos (áreas clicáveis) dentro — ver DescriptionForm. */
export const PermissionRadioWrapper: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex w-full max-w-[220px] gap-1 rounded-lg border border-solid border-slate-200 bg-white p-1",
      className
    )}
    {...props}
  />
);

/** Selects do cadastro — bordas arredondadas e foco visível */
export const formSelectStyles: StylesConfig<any, false> = {
  container: (base) => ({ ...base, width: "100%", marginBottom: 12 }),
  control: (base, state) => {
    const color = state.isFocused ? "#94a3b8" : "#e2e8f0";
    const hoverColor = state.isFocused ? "#94a3b8" : "#cbd5e1";
    return {
      ...base,
      alignItems: "center",
      minHeight: 44,
      cursor: "pointer",
      borderRadius: 6,
      outline: "none",
      backgroundColor: "#ffffff",
      boxShadow: "none",
      border: `1px solid ${color}`,
      transition: "border-color 0.12s ease",
      "&:hover": {
        border: `1px solid ${hoverColor}`,
        boxShadow: "none",
      },
    };
  },
  input: (base) => ({
    ...base,
    boxShadow: "none",
    outline: "none",
  }),
  valueContainer: (base) => ({ ...base, paddingLeft: 10, paddingRight: 6 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 10001, borderRadius: 6, overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)" }),
  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
  option: (base, state) => ({
    ...base,
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#f1f5f9"
      : state.isFocused
        ? "#f8fafc"
        : "white",
    color: "#1e293b",
  }),
  singleValue: (base) => ({ ...base, color: "#1e293b", fontWeight: 500 }),
  placeholder: (base) => ({ ...base, color: "#94a3b8" }),
};

const inputFlat =
  "[&>input:not([type='radio']):not([type='checkbox'])]:mb-5 [&>input:not([type='radio']):not([type='checkbox'])]:w-full [&>input:not([type='radio']):not([type='checkbox'])]:appearance-none [&>input:not([type='radio']):not([type='checkbox'])]:rounded-md [&>input:not([type='radio']):not([type='checkbox'])]:border [&>input:not([type='radio']):not([type='checkbox'])]:border-solid [&>input:not([type='radio']):not([type='checkbox'])]:border-slate-200 [&>input:not([type='radio']):not([type='checkbox'])]:bg-white [&>input:not([type='radio']):not([type='checkbox'])]:px-3 [&>input:not([type='radio']):not([type='checkbox'])]:py-2 [&>input:not([type='radio']):not([type='checkbox'])]:text-sm [&>input:not([type='radio']):not([type='checkbox'])]:text-slate-800 [&>input:not([type='radio']):not([type='checkbox'])]:shadow-none [&>input:not([type='radio']):not([type='checkbox'])]:outline-none [&>input:not([type='radio']):not([type='checkbox'])]:transition [&>input:not([type='radio']):not([type='checkbox'])]:placeholder:text-slate-400 [&>input:not([type='radio']):not([type='checkbox']):focus]:border-slate-400 [&>input:not([type='radio']):not([type='checkbox']):focus]:shadow-none [&>input:not([type='radio']):not([type='checkbox']):focus]:ring-0";

export const Form: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "w-full rounded-md border border-slate-200 bg-white p-5 sm:p-8",
      "[&>p]:mb-2 [&>p]:mt-7 [&>p]:flex [&>p]:items-end [&>p]:justify-start [&>p]:text-sm [&>p]:font-semibold [&>p]:text-slate-700",
      "[&>p>p]:ml-1.5 [&>p>p]:text-[10px] [&>p>p]:font-bold",
      "[&>#initial-label]:flex [&>#initial-label]:items-center [&>#initial-label]:justify-center",
      inputFlat,
      "[&>textarea]:mb-5 [&>textarea]:h-[200px] [&>textarea]:w-full [&>textarea]:resize-y [&>textarea]:appearance-none [&>textarea]:rounded-md [&>textarea]:border [&>textarea]:border-solid [&>textarea]:border-slate-200 [&>textarea]:bg-white [&>textarea]:p-3 [&>textarea]:text-sm [&>textarea]:text-slate-800 [&>textarea]:shadow-none [&>textarea]:outline-none [&>textarea]:transition [&>textarea:focus]:border-slate-400 [&>textarea:focus]:shadow-none [&>textarea:focus]:ring-0",
      "max-md:[&>p]:flex-col max-md:[&>p]:items-start max-md:[&>p>p]:ml-0",
      className
    )}
    {...props}
  />
);

/** CreateProperty: opções em linha (row), coluna só no mobile — como styled original */
export const PropertyTypesContainer: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "mx-auto mt-4 flex w-full max-w-4xl flex-row flex-wrap items-center justify-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-4 max-md:flex-col max-md:items-stretch max-md:justify-start",
      className
    )}
    {...props}
  />
);

export const OwnerList: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "relative w-full min-w-0",
      /* Borda única no ListAddWrapper; input sem borda própria */
      "[&_input]:h-full [&_input]:min-h-0 [&_input]:min-w-0 [&_input]:w-full [&_input]:flex-1 [&_input]:appearance-none [&_input]:border-0 [&_input]:bg-transparent [&_input]:px-0 [&_input]:py-0 [&_input]:text-sm [&_input]:leading-normal [&_input]:text-slate-800 [&_input]:shadow-none [&_input]:outline-none [&_input]:transition",
      "[&_input:focus]:ring-0",
      "[&_svg]:h-6 [&_svg]:w-6 [&_svg]:shrink-0 [&_svg]:cursor-pointer [&_svg]:text-[#dd6435]",
      "[&_span_svg]:h-4 [&_span_svg]:w-4 [&_span_svg]:text-[#b6b6b6]",
      "[&_.css-2b097c-container]:w-full [&_.css-2b097c-container:last-child]:mb-16",
      "[&_[class*='-control']]:rounded-md [&_[class*='-control']]:border [&_[class*='-control']]:border-solid [&_[class*='-control']]:border-slate-200 [&_[class*='-control']]:bg-white [&_[class*='-control']]:shadow-none",
      "[&_#users]:absolute [&_#users]:left-0 [&_#users]:right-0 [&_#users]:top-full [&_#users]:z-[10000] [&_#users]:mt-1 [&_#users]:flex [&_#users]:max-h-[350px] [&_#users]:flex-col [&_#users]:items-stretch [&_#users]:justify-start [&_#users]:overflow-y-auto [&_#users]:rounded-md [&_#users]:border [&_#users]:border-[#b6b6b6] [&_#users]:bg-white [&_#users]:shadow-lg",
      "[&_#users>button]:w-full [&_#users>button]:border-0 [&_#users>button]:bg-white [&_#users>button]:px-4 [&_#users>button]:py-2.5 [&_#users>button]:text-left [&_#users>button]:text-sm [&_#users>button]:text-[#333] [&_#users>button]:transition-colors [&_#users>button]:duration-200 [&_#users>button:hover]:bg-[#F9F9F9]",
      "max-md:[&_.css-2b097c-container]:w-full",
      className
    )}
    {...props}
  />
);

/** Borda do bloco proprietário (input + ícones): mesma espessura/cor que `inputFlat` no `Form`. */
export const ListAddWrapper: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "box-border flex h-10 w-full min-w-0 items-center gap-2 rounded-md border border-solid border-slate-200 bg-white px-3 outline-none ring-0 transition",
      "focus-within:border-slate-400 focus-within:outline-none focus-within:ring-0 focus-within:ring-offset-0",
      "[&>input]:min-w-0 [&>input]:flex-1",
      className
    )}
    {...props}
  />
);

export const PropertyPermissionsWrapper: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-6 mb-12 flex w-full flex-wrap content-start items-start justify-start gap-x-10 gap-y-5 rounded-md border border-slate-200 bg-slate-50 p-5 max-md:flex-col max-md:items-stretch max-md:gap-y-4",
      className
    )}
    {...props}
  />
);

export const PermissionsLabelRadiosWrapper: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex w-[min(100%,220px)] shrink-0 flex-col items-start gap-1",
      className
    )}
    {...props}
  />
);

export const IAButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className, type = "button", ...props }) => (
  <button
    type={type}
    className={cn(
      "flex w-fit cursor-pointer items-center gap-2 rounded-md border-0 bg-[#6EA5FF] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#5c94ed] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:h-[18px] [&_svg]:w-[18px]",
      className
    )}
    {...props}
  />
);

export const HelpText: React.FC<
  React.HTMLAttributes<HTMLParagraphElement>
> = ({ className, children, ...props }) => (
  <p
    className={cn(
      "mt-2 text-[13px] text-[#666666] [&_a]:ml-1 [&_a]:font-medium [&_a]:text-[#3b82f6] [&_a]:no-underline [&_a]:transition-opacity [&_a:hover]:opacity-70",
      className
    )}
    {...props}
  >
    {children}
  </p>
);

/** Cadastro: sem herdar `border-b` só do Edit — borda completa como os demais inputs. */
export const CondominiumFieldWrapper: React.FC<DivProps> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "my-5 w-full",
      "[&>input]:w-full [&>input]:appearance-none [&>input]:rounded-md [&>input]:border [&>input]:border-solid [&>input]:border-slate-200 [&>input]:bg-white [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:shadow-none [&>input]:outline-none [&>input]:transition",
      "[&>input:focus]:border-slate-400 [&>input:focus]:shadow-none [&>input:focus]:ring-0",
      className
    )}
    {...props}
  />
);
